import React, { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Newspaper, Mic, Quote, ChevronLeft, ChevronRight, Share2, Flame, TrendingUp } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import InterviewModal from "@/components/InterviewModal";

const PAGE_SIZE = 15;

function publicFileUrl(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return null;
  return `${base}/api/public/files/${imagePath}`;
}

const TONE_META = {
  fiducia:       { label: "FIDUCIA",    color: "#0ea5e9", emoji: "💪", gradient: "linear-gradient(135deg,#38bdf8,#0284c7)" },
  rabbia:        { label: "SFOGO",      color: "#ef4444", emoji: "🔥", gradient: "linear-gradient(135deg,#f97316,#dc2626)" },
  ironia:        { label: "SFOTTÒ",     color: "#f59e0b", emoji: "😏", gradient: "linear-gradient(135deg,#fbbf24,#f59e0b)" },
  dichiarazione: { label: "ESCLUSIVA",  color: "#3b56e0", emoji: "📣", gradient: "linear-gradient(135deg,#6366f1,#3b56e0)" },
  disperazione:  { label: "DISPERAZIONE", color: "#8b5cf6", emoji: "😭", gradient: "linear-gradient(135deg,#a78bfa,#6d28d9)" },
  resa:          { label: "RESA",       color: "#64748b", emoji: "🏳️", gradient: "linear-gradient(135deg,#94a3b8,#475569)" },
};

const initials = (n = "") => n.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("") || "FIFE";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s fa`;
  if (s < 3600) return `${Math.floor(s / 60)}m fa`;
  if (s < 86400) return `${Math.floor(s / 3600)}h fa`;
  return `${Math.floor(s / 86400)}g fa`;
}

export default function News() {
  const [items, setItems] = useState([]);
  const [viral, setViral] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const loadViral = () => {
    api.get("/public/interviews/viral?limit=5")
      .then(({ data }) => setViral(data))
      .catch(() => setViral([]));
  };

  useEffect(() => {
    api.get("/public/interviews?limit=500")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
    loadViral();
  }, []);

  // Deep-link: /news/:id → apre il modale con l'articolo giusto.
  // Se il feed principale non lo contiene (>500 elementi), fetch dedicato.
  useEffect(() => {
    if (!routeId) { setSelected(null); return; }
    const inList = items.find((i) => i.id === routeId);
    if (inList) { setSelected(inList); return; }
    if (!loaded) return; // aspetta il feed
    api.get(`/public/interviews/${routeId}`)
      .then(({ data }) => setSelected(data))
      .catch(() => {
        toast.error("Articolo non trovato o rimosso");
        navigate("/news", { replace: true });
      });
  }, [routeId, items, loaded, navigate]);

  const openInterview = (it) => {
    setSelected(it);
    navigate(`/news/${it.id}`);
  };
  const closeInterview = () => {
    setSelected(null);
    if (routeId) navigate("/news");
  };

  const shareInterview = async (e, it) => {
    e.stopPropagation();
    // URL con OG meta tag (WhatsApp/Facebook mostrano preview con immagine 1080x1080).
    // Usiamo l'origine corrente (window.location.origin) invece di REACT_APP_BACKEND_URL
    // così il link condiviso resta sempre sullo stesso host da cui l'utente lo genera
    // (es. https://fantafife.com in prod). L'ingress inoltra `/api/*` al backend che
    // serve gli OG e poi redirige a `/news/:id` sulla SPA.
    const url = `${window.location.origin}/api/public/share/news/${it.id}`;
    const shareData = {
      title: `${it.team_name} · FIFE`,
      text: `«${it.headline}» — ${it.coach_name || it.team_name}`,
      url,
    };
    // Incrementa contatore condivisioni (best-effort, non blocca lo share)
    api.post(`/public/interviews/${it.id}/share`)
      .then(() => loadViral())
      .catch(() => {});
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiato negli appunti");
      } else {
        window.prompt("Copia il link:", url);
      }
    } catch (err) {
      // Utente ha annullato lo share sheet: nessun toast.
      if (err && err.name !== "AbortError") {
        toast.error("Condivisione non riuscita");
      }
    }
  };

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((i) => i.tone === filter)),
    [items, filter],
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Reset a pag. 1 quando cambia il filtro
  useEffect(() => { setPage(1); }, [filter]);

  const goToPage = (p) => {
    setPage(p);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-6">
        <div className="fade-up flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "#3b56e022", color: "#3b56e0" }}>
            <Newspaper className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Sala stampa FIFE</div>
            <h1 className="display font-black text-4xl md:text-5xl">News</h1>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2" data-testid="news-filters">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="news-filter-all">Tutti · {items.length}</FilterChip>
          {Object.entries(TONE_META).map(([id, t]) => {
            const n = items.filter((i) => i.tone === id).length;
            return (
              <FilterChip key={id} active={filter === id} onClick={() => setFilter(id)} color={t.color} testid={`news-filter-${id}`}>
                {t.emoji} {t.label} · {n}
              </FilterChip>
            );
          })}
        </div>

        {/* Articoli virali */}
        {viral.length > 0 && (
          <div className="card-surface rounded-2xl p-4 md:p-5" data-testid="news-viral-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center"
                   style={{ background: "linear-gradient(135deg,#f97316,#dc2626)" }}>
                <Flame className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Trending</div>
                <div className="display font-black text-lg leading-none">Articoli virali</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {viral.map((it, idx) => {
                const t = TONE_META[it.tone] || TONE_META.dichiarazione;
                return (
                  <button key={it.id} type="button"
                          onClick={() => openInterview(it)}
                          className="text-left rounded-xl p-3 border border-slate-200 hover:border-orange-300 hover:bg-orange-50/30 transition flex items-start gap-2 min-w-0"
                          data-testid={`news-viral-${it.id}`}>
                    <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black tabular-nums"
                         style={{ background: t.color }}>
                      #{idx + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{it.team_name}</div>
                      <div className="text-xs font-bold text-slate-900 line-clamp-2 leading-snug break-words">«{it.headline}»</div>
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-black text-orange-600">
                        <TrendingUp className="w-3 h-3" />
                        <span className="tabular-nums">{it.share_count}</span>
                        <span className="text-slate-400 font-normal">condivisioni</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loaded && filtered.length === 0 ? (
          <div className="card-surface rounded-2xl p-10 text-center" data-testid="news-empty">
            <Mic className="w-8 h-8 mx-auto mb-3 text-slate-400" />
            <div className="display font-bold text-slate-700">Nessun articolo per questo filtro.</div>
            <div className="text-slate-500 text-sm mt-1">
              I fantallenatori pubblicano conferenze dalla loro <Link to="/dashboard" className="text-[var(--brand)] font-semibold hover:underline">area riservata</Link>.
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr stagger" data-testid="news-grid">
              {pageItems.map((it) => {
              const t = TONE_META[it.tone] || TONE_META.dichiarazione;
              const img = it.image_path ? publicFileUrl(it.image_path) : null;
              return (
                <div key={it.id} className="card-surface rounded-2xl overflow-hidden card-lift text-left h-full flex flex-col group relative"
                     data-testid={`news-card-${it.id}`}>
                  <button type="button" onClick={() => openInterview(it)}
                          className="text-left h-full flex flex-col flex-1 min-w-0"
                          aria-label={`Apri articolo: ${it.headline}`}>
                    <div className="aspect-[16/9] relative overflow-hidden shrink-0" style={{ background: t.gradient }}>
                      {img ? (
                        <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <>
                          <div className="absolute inset-0 opacity-20"
                               style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0 12px, transparent 12px 28px)" }} />
                          <Quote className="absolute top-3 left-3 w-8 h-8 text-white/40" />
                          <div className="absolute inset-0 flex items-center justify-center display font-black text-white text-5xl" style={{ textShadow: "0 4px 24px rgba(0,0,0,0.35)" }}>
                            {initials(it.team_name)}
                          </div>
                        </>
                      )}
                      <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase tracking-widest text-white" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>{t.emoji} {t.label}</span>
                        <span className="text-[10px] text-white/90" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}>{timeAgo(it.created_at)}</span>
                      </div>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 break-words truncate">{it.team_name} · Girone {it.girone}</div>
                      <h3 className="display font-black text-lg mt-1 leading-snug text-slate-900 break-words line-clamp-2 min-h-[3.25rem]">«{it.headline}»</h3>
                      <p className="text-slate-600 text-sm mt-2 italic leading-relaxed line-clamp-3 break-words [overflow-wrap:anywhere] min-h-[3.75rem]">{it.quote}</p>
                      {it.coach_name && <div className="text-xs text-slate-500 mt-auto pt-3 break-words truncate">— {it.coach_name}</div>}
                    </div>
                  </button>
                  {/* Share button flottante — evita di intercettare il click della card */}
                  <button type="button" onClick={(e) => shareInterview(e, it)}
                          aria-label="Condividi articolo"
                          title="Condividi"
                          className="absolute top-3 right-3 z-10 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/95 hover:bg-white text-slate-800 shadow-md ring-1 ring-black/5 backdrop-blur transition hover:scale-105"
                          data-testid={`news-share-${it.id}`}>
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
            </div>
            {totalPages > 1 && (
              <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} totalItems={filtered.length} />
            )}
          </>
        )}
      </div>
      <InterviewModal interview={selected} onClose={closeInterview} />
      <Footer />
    </div>
  );
}

function FilterChip({ children, active, onClick, color, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[11px] font-black uppercase tracking-widest transition
              ${active ? "text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            style={active ? { background: color || "#3b56e0" } : undefined}>
      {children}
    </button>
  );
}

/** Pagination bar — mostra Prev, numeri pagina con ellissi, Next + counter totale. */
function Pagination({ page, totalPages, onChange, totalItems }) {
  const nums = pageNumbers(page, totalPages);
  return (
    <nav className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-6 border-t border-slate-200"
         aria-label="Paginazione articoli" data-testid="news-pagination">
      <div className="text-xs text-slate-500 tabular-nums" data-testid="news-pagination-info">
        Pagina <b className="text-slate-900">{page}</b> di <b className="text-slate-900">{totalPages}</b>
        <span className="ml-2 text-slate-400">·</span>
        <span className="ml-2">{totalItems} articoli</span>
      </div>
      <div className="flex items-center gap-1 flex-wrap justify-center">
        <button type="button" onClick={() => onChange(page - 1)} disabled={page <= 1}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="news-pagination-prev">
          <ChevronLeft className="w-4 h-4" /> Prev
        </button>
        {nums.map((n, i) => n === "…" ? (
          <span key={`e${i}`} className="px-1.5 text-slate-400 tabular-nums text-xs">…</span>
        ) : (
          <button key={n} type="button" onClick={() => onChange(n)}
                  className={`min-w-9 h-9 px-3 rounded-full text-xs font-black tabular-nums transition ${n === page ? "text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  style={n === page ? { background: "#3b56e0" } : undefined}
                  data-testid={`news-pagination-page-${n}`}
                  aria-current={n === page ? "page" : undefined}>
            {n}
          </button>
        ))}
        <button type="button" onClick={() => onChange(page + 1)} disabled={page >= totalPages}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="news-pagination-next">
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </nav>
  );
}

/** Ritorna [1, "…", n-1, n, n+1, "…", totalPages] o similare, compatto e user-friendly. */
function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const arr = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    out.push(arr[i]);
    if (i < arr.length - 1 && arr[i + 1] - arr[i] > 1) out.push("…");
  }
  return out;
}
